"use client";

import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Stack,
  IconButton,
  Button,
  Drawer,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Link from "next/link";
import { navLinks } from "@/shared/constants";
import Logo from "./Logo";
import { usePathname } from "next/navigation";
import { useClientMediaQuery } from "@/hooks/useClientMediaQuery";

export default function Header() {
  const theme = useTheme();
  const isMobile = useClientMediaQuery("(max-width:1024px)", false);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const pathname = usePathname();

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const NavLinks = (
    <Stack
      direction="row"
      spacing={{
        xs: 1.5,
        sm: 2,
        md: 2.5,
        lg: 3,
        xl: 4,
      }}
      alignItems="center"
      sx={{
        flexWrap: "nowrap",
        flexGrow: 1,
        justifyContent: "flex-end",
        minWidth: 0,
      }}
    >
      {navLinks.map((link) => (
        <Button
          key={link.label}
          component={Link}
          href={link.href}
          disableRipple
          sx={{
            backgroundColor:
              pathname === link.href ? "rgba(12, 29, 3, 0.1)" : "transparent",
            borderRadius: "6px",
            px: 1.5,
            py: 0.5,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            fontSize: "18px",
            textTransform: "none",
            width: isMobile ? "100%" : "auto",
            justifyContent: isMobile ? "flex-start" : "center",
            "&:hover": {
              background: "transparent",
            },
          }}
        >
          {link.label}
        </Button>
      ))}
      <Button
        variant="contained"
        fullWidth={isMobile}
        href={"sign-up"}
        sx={{
          backgroundColor: "secondary.main",
          color: "secondary.contrastText",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          fontSize: {
            xs: "16px",
            sm: "16.5px",
            md: "17px",
            lg: "18px",
          },
          height: "41.7px",
          px: {
            xs: 2,
            sm: 4,
            md: 5,
            lg: 6,
          },
          whiteSpace: "nowrap",
          borderRadius: "4px",
          mt: isMobile ? 2 : 0,
          "&:hover": {
            backgroundColor: "secondary.main",
          },
        }}
      >
        Sign Up
      </Button>
    </Stack>
  );

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: "background.default",
        boxShadow: "0 12px 16px rgba(0, 0, 0, 0.03)",
        px: "65.5px",
        ...(isMobile && { px: "16px" }),
        py: "17px",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          width: "100%",
          maxWidth: "1366px",
          mx: "auto",
          height: "80px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Logo */}

        <Logo
          alt="Nestshipper Logo"
          width={229}
          height={47}
          style={
            isMobile
              ? { width: 193, height: 33, flexShrink: 0 }
              : { flexShrink: 0 }
          }
          priority
        />
        {/* Desktop Navigation */}
        {!isMobile && NavLinks}

        {/* Mobile Hamburger */}
        {isMobile && (
          <IconButton onClick={toggleDrawer(true)}>
            <MenuIcon sx={{ color: "primary.main", fontSize: "2rem" }} />
          </IconButton>
        )}
      </Toolbar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "100%",
            backgroundColor: "#fff",
            p: 0,
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            px: "56px",
            pt: "59px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Top Row: Close Icon + Logo */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={6}
          >
            <IconButton
              onClick={toggleDrawer(false)}
              sx={{
                p: "6px",
                border: "3px solid",
                borderColor: "primary.main",
                borderRadius: "4px",
                width: "22px",
                height: "22px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloseIcon
                sx={{
                  color: "primary.main",
                  fontSize: "14px",
                  stroke: "currentColor",
                  strokeWidth: 1,
                }}
              />
            </IconButton>

            <Logo variant="square" width={40} height={40} />
          </Box>

          <Box
            sx={{
              width: "146px",
              height: "auto",
              mb: "37px",
              ml: 0,
              mr: "auto",
            }}
          >
            <Stack spacing={1}>
              {navLinks.map((link) => (
                <Button
                  key={link.label}
                  component={Link}
                  href={link.href}
                  onClick={toggleDrawer(false)}
                  disableRipple
                  sx={{
                    borderRadius: "6px",
                    fontWeight: 500,
                    fontSize: "18px",
                    textTransform: "none",
                    px: 1.5,
                    fontFamily: "Poppins, sans-serif",
                    justifyContent: "flex-start",
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          </Box>

          <Button
            variant="contained"
            href={"sign-up"}
            sx={{
              backgroundColor: "secondary.main",
              color: "secondary.contrastText",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              height: "41.7px",
              width: "100%",
              px: "115.5px",
              alignSelf: "center",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "secondary.main",
              },
            }}
          >
            Sign Up
          </Button>
        </Box>
      </Drawer>
    </AppBar>
  );
}
